import { useState, useEffect } from 'react'
import { useCanAccess } from '../../hooks/useCanAccess'
import { useClientStore } from '../../stores/clientStore'
import { badgeApi } from '../../services/badgeApi'
import type { Badge } from '../../types'
import '../../styles/admin.css'

export default function BadgeManagement() {
  const canCreate = useCanAccess('badges', 'create')
  const canUpdate = useCanAccess('badges', 'update')
  const canDelete = useCanAccess('badges', 'delete')
  const clients = useClientStore((s) => s.clients)

  const [badges, setBadges] = useState<Badge[]>([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [form, setForm] = useState({
    badge_code: '',
    name: '',
    client_id: '',
    tb_device_name: '',
    status: 'active',
    default_floor: 0,
  })

  const fetchBadges = async () => {
    setLoading(true)
    try {
      const data = await badgeApi.getAllBadges()
      setBadges(data)
    } catch (e) {
      console.error('Failed to load badges:', e)
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchBadges()
  }, [])

  const openCreate = () => {
    setForm({
      badge_code: '',
      name: '',
      client_id: clients[0]?.id ?? '',
      tb_device_name: '',
      status: 'active',
      default_floor: 0,
    })
    setEditingId(null)
    setError(null)
    setShowModal(true)
  }

  const openEdit = (badge: Badge) => {
    setForm({
      badge_code: badge.badge_code,
      name: badge.name,
      client_id: badge.client_id,
      tb_device_name: badge.tb_device_name ?? '',
      status: badge.status,
      default_floor: badge.default_floor,
    })
    setEditingId(badge.id)
    setError(null)
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!form.badge_code.trim() || !form.name.trim()) {
      setError('Code et nom sont obligatoires')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (editingId) {
        await badgeApi.updateBadge(editingId, {
          badge_code: form.badge_code.trim(),
          name: form.name.trim(),
          client_id: form.client_id || undefined,
          tb_device_name: form.tb_device_name.trim() || undefined,
          status: form.status,
          default_floor: form.default_floor,
        })
      } else {
        if (!form.client_id) {
          setError('Le client est obligatoire')
          setSaving(false)
          return
        }
        await badgeApi.createBadge({
          badge_code: form.badge_code.trim(),
          name: form.name.trim(),
          client_id: form.client_id,
          tb_device_name: form.tb_device_name.trim() || undefined,
          status: form.status,
          default_floor: form.default_floor,
        })
      }
      await fetchBadges()
      setShowModal(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (badge: Badge) => {
    if (!confirm(`Supprimer le badge "${badge.name}" (${badge.badge_code}) ?`)) return
    try {
      await badgeApi.deleteBadge(badge.id)
      await fetchBadges()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur suppression')
    }
  }

  const statusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'admin-badge--green'
      case 'inactive':
        return 'admin-badge--red'
      default:
        return 'admin-badge--blue'
    }
  }

  if (loading) {
    return <div className="admin-loading">Chargement des badges...</div>
  }

  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3>Badges</h3>
        <div className="admin-section__actions">
          {canCreate && (
            <button className="admin-btn admin-btn--create" onClick={openCreate}>
              Nouveau badge
            </button>
          )}
        </div>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Nom</th>
            <th>Client</th>
            <th>Device TB</th>
            <th>Status</th>
            <th>Etage</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {badges.map((b) => (
            <tr key={b.id}>
              <td><code>{b.badge_code}</code></td>
              <td>{b.name}</td>
              <td>{clients.find((c) => c.id === b.client_id)?.name ?? '-'}</td>
              <td>{b.tb_device_name ?? '-'}</td>
              <td>
                <span className={`admin-badge ${statusLabel(b.status)}`}>
                  {b.status}
                </span>
              </td>
              <td>{b.default_floor}</td>
              <td className="admin-table__actions">
                {canUpdate && (
                  <button className="admin-btn admin-btn--small" onClick={() => openEdit(b)}>
                    Editer
                  </button>
                )}
                {canDelete && (
                  <button className="admin-btn admin-btn--small admin-btn--danger" onClick={() => handleDelete(b)}>
                    Supprimer
                  </button>
                )}
              </td>
            </tr>
          ))}
          {badges.length === 0 && (
            <tr><td colSpan={7} className="admin-table__empty">Aucun badge</td></tr>
          )}
        </tbody>
      </table>

      {showModal && (
        <div className="admin-modal__overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? 'Editer badge' : 'Nouveau badge'}</h3>
            <div className="admin-form">
              <div className="admin-form__field">
                <label>Code badge</label>
                <input
                  type="text"
                  value={form.badge_code}
                  onChange={(e) => setForm((p) => ({ ...p, badge_code: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="admin-form__field">
                <label>Nom</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              {clients.length > 0 && (
                <div className="admin-form__field">
                  <label>Client</label>
                  <select
                    value={form.client_id}
                    onChange={(e) => setForm((p) => ({ ...p, client_id: e.target.value }))}
                  >
                    <option value="">-- Aucun --</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="admin-form__field">
                <label>Device ThingsBoard</label>
                <input
                  type="text"
                  value={form.tb_device_name}
                  onChange={(e) => setForm((p) => ({ ...p, tb_device_name: e.target.value }))}
                />
              </div>
              <div className="admin-form__field">
                <label>Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
              <div className="admin-form__field">
                <label>Etage par defaut</label>
                <input
                  type="number"
                  value={form.default_floor}
                  onChange={(e) => setForm((p) => ({ ...p, default_floor: parseInt(e.target.value) || 0 }))}
                />
              </div>
              {error && <div className="admin-error">{error}</div>}
              <div className="admin-form__actions">
                <button className="admin-btn" onClick={() => setShowModal(false)}>Annuler</button>
                <button
                  className="admin-btn admin-btn--create"
                  onClick={handleSave}
                  disabled={saving || !form.badge_code.trim() || !form.name.trim()}
                >
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
